/**
 * PDFViewerModal
 *
 * Full-screen modal for viewing PDF documents.
 * Supports viewing PDF attachments and generated PDFs.
 * Works across Web, iOS, and Android platforms.
 */

import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  XCircle,
  Share2,
} from 'lucide-react-native';
import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ActivityIndicator,
  Platform,
  SafeAreaView,
  Alert,
} from 'react-native';
import * as RNFS from 'react-native-fs';
import Share from 'react-native-share';

import { PDFViewer } from './PDFViewer';
import { downloadPDF } from '../../../services/pdfService';
import { pdfViewerService } from '../../../services/pdfViewerService';
import { PDFViewerModalProps, PDFViewerState } from '../../../types/pdf';
import { showToast } from '../../../utils/toast';

// @ts-ignore - react-native-share is mocked on web

export const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  visible,
  onClose,
  source,
  title,
  loading,
  onDownload,
  hideDownload = false,
  downloadFileName,
}) => {
  const [state, setState] = useState<PDFViewerState>({
    currentPage: 1,
    totalPages: 0,
    scale: 1.0,
    isLoading: true,
    error: null,
  });
  const [normalizedSource, setNormalizedSource] = useState<string | null>(null);

  // Normalize PDF source when it changes
  useEffect(() => {
    if (!source || !visible) {
      setNormalizedSource(null);
      return;
    }

    const loadSource = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        const normalized = await pdfViewerService.normalizePDFSource(source);
        setNormalizedSource(normalized);
      } catch (error) {
        console.error('Error normalizing PDF source:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load PDF',
        }));
      }
    };

    loadSource();
  }, [source, visible]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setState({
        currentPage: 1,
        totalPages: 0,
        scale: 1.0,
        isLoading: true,
        error: null,
      });
      setNormalizedSource(null);
    }
  }, [visible]);

  const handleDownload = useCallback(async () => {
    if (onDownload) {
      onDownload();
      return;
    }

    if (!normalizedSource) return;

    try {
      const fileName = downloadFileName || source?.fileName || 'document.pdf';
      const result = await downloadPDF(normalizedSource, fileName);
      if (Platform.OS === 'android') {
        showToast.success(result.message || 'PDF saved to Downloads folder');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      Alert.alert(
        'Download Failed',
        'Could not save the PDF. Try using the Share button instead.',
      );
    }
  }, [normalizedSource, onDownload, downloadFileName, source?.fileName]);

  const handleShare = useCallback(async () => {
    if (Platform.OS === 'web' || !normalizedSource) return;

    try {
      const fileName = downloadFileName || source?.fileName || 'document.pdf';

      // Extract base64 data from the data URI
      const base64Data = normalizedSource.split(',')[1];
      if (!base64Data) {
        console.error('Invalid PDF data URI for sharing');
        return;
      }

      // Save to temporary file
      const tempPath = `${RNFS.CachesDirectoryPath}/${fileName}`;
      await RNFS.writeFile(tempPath, base64Data, 'base64');

      // Open native share sheet
      await Share.open({
        title: 'Share PDF',
        url: `file://${tempPath}`,
        type: 'application/pdf',
        subject: fileName.replace('.pdf', ''),
      });

      // Clean up temp file after sharing (with delay to ensure share completes)
      setTimeout(async () => {
        try {
          const exists = await RNFS.exists(tempPath);
          if (exists) {
            await RNFS.unlink(tempPath);
          }
        } catch (cleanupError) {
          console.log('Temp file cleanup skipped:', cleanupError);
        }
      }, 5000);
    } catch (error: any) {
      // User cancelled share sheet - this is not an error
      if (
        error?.message?.includes('User did not share') ||
        error?.message?.includes('CANCELLED')
      ) {
        console.log('Share cancelled by user');
        return;
      }
      console.error('Error sharing PDF:', error);
    }
  }, [normalizedSource, downloadFileName, source?.fileName]);

  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  }, []);

  const handleLoadComplete = useCallback((totalPages: number) => {
    setState(prev => ({ ...prev, totalPages, isLoading: false }));
  }, []);

  const handleError = useCallback((error: Error) => {
    console.error('PDF viewer error:', error);
    setState(prev => ({
      ...prev,
      isLoading: false,
      error: error.message || 'Failed to load PDF',
    }));
  }, []);

  const goToPreviousPage = useCallback(() => {
    if (state.currentPage > 1) {
      handlePageChange(state.currentPage - 1);
    }
  }, [state.currentPage, handlePageChange]);

  const goToNextPage = useCallback(() => {
    if (state.currentPage < state.totalPages) {
      handlePageChange(state.currentPage + 1);
    }
  }, [state.currentPage, state.totalPages, handlePageChange]);

  const handleZoomIn = useCallback(() => {
    setState(prev => ({ ...prev, scale: Math.min(prev.scale + 0.25, 3.0) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setState(prev => ({ ...prev, scale: Math.max(prev.scale - 0.25, 0.5) }));
  }, []);

  const displayTitle = title || source?.fileName || 'PDF Viewer';

  // Categorize and get user-friendly error messages
  const getErrorInfo = (
    errorMessage: string,
  ): { title: string; suggestion: string } => {
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('Network')
    ) {
      return {
        title: 'Network error occurred',
        suggestion: 'Please check your connection and try again.',
      };
    }
    if (
      errorMessage.includes('memory') ||
      errorMessage.includes('size') ||
      errorMessage.includes('large')
    ) {
      return {
        title: 'PDF is too large to display',
        suggestion: 'Please download the file to view it.',
      };
    }
    if (
      errorMessage.includes('invalid') ||
      errorMessage.includes('corrupt') ||
      errorMessage.includes('Invalid')
    ) {
      return {
        title: 'PDF file appears to be invalid',
        suggestion: 'The file may be corrupted. Try re-uploading.',
      };
    }
    return {
      title: 'Unable to load PDF',
      suggestion: 'Please try again or download the file instead.',
    };
  };

  const renderError = () => {
    if (!state.error) return null;

    const { title: errorTitle, suggestion } = getErrorInfo(state.error);

    return (
      <View style={styles.errorContainer}>
        <View style={styles.errorIconContainer}>
          <XCircle size={48} color="#dc3545" />
        </View>
        <Text style={styles.errorTitle}>{errorTitle}</Text>
        <Text style={styles.errorText}>{suggestion}</Text>
        <View style={styles.errorButtons}>
          {hideDownload ? null : (
            <TouchableOpacity
              style={styles.downloadButton}
              onPress={handleDownload}
              accessibilityLabel="Download PDF instead"
            >
              <Download size={16} color="#fff" />
              <Text style={styles.downloadButtonText}>Download Instead</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.closeErrorButton}
            onPress={onClose}
            accessibilityLabel="Close"
          >
            <Text style={styles.closeErrorButtonText}>Close</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={onClose}
            style={styles.headerButton}
            accessibilityLabel="Close PDF viewer"
          >
            <X size={24} color="#333" />
          </TouchableOpacity>

          <Text style={styles.title} numberOfLines={2}>
            {displayTitle}
          </Text>

          <View style={styles.headerActions}>
            {Platform.OS !== 'web' && (
              <TouchableOpacity
                onPress={handleShare}
                style={styles.headerButton}
                accessibilityLabel="Share PDF"
              >
                <Share2 size={24} color="#007AFF" />
              </TouchableOpacity>
            )}
            {hideDownload ? null : (
              <TouchableOpacity
                onPress={handleDownload}
                style={styles.headerButton}
                accessibilityLabel="Download PDF"
              >
                <Download size={24} color="#007AFF" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Content */}
        {Platform.OS === 'web' ? (
          // Use native HTML div on web to avoid react-native-web View conflicts with react-pdf
          <div
            style={{
              flex: 1,
              backgroundColor: '#f5f5f5',
              position: 'relative',
              overflow: 'auto',
              minHeight: 0,
            }}
          >
            {state.error && renderError()}

            {normalizedSource && !state.error && (
              <PDFViewer
                source={normalizedSource}
                page={state.currentPage}
                scale={state.scale}
                onPageChange={handlePageChange}
                onLoadComplete={handleLoadComplete}
                onError={handleError}
              />
            )}

            {(state.isLoading || loading) && !state.error && (
              <View style={styles.loadingOverlay}>
                <ActivityIndicator size="large" color="#FF6B00" />
                <Text style={styles.loadingText}>Loading PDF...</Text>
              </View>
            )}
          </div>
        ) : (
          <View style={styles.content}>
            {state.error && renderError()}

            {normalizedSource && !state.error && (
              <PDFViewer
                source={normalizedSource}
                page={state.currentPage}
                scale={state.scale}
                onPageChange={handlePageChange}
                onLoadComplete={handleLoadComplete}
                onError={handleError}
              />
            )}

            {(state.isLoading || loading) && !state.error && (
              <View style={styles.loadingOverlay}>
                <ActivityIndicator size="large" color="#FF6B00" />
                <Text style={styles.loadingText}>Loading PDF...</Text>
              </View>
            )}
          </View>
        )}

        {/* Footer Controls */}
        {!state.isLoading &&
          !loading &&
          !state.error &&
          state.totalPages > 0 && (
            <View style={styles.footer}>
              {/* Page Navigation */}
              <View style={styles.pageControls}>
                <TouchableOpacity
                  onPress={goToPreviousPage}
                  disabled={state.currentPage <= 1}
                  style={[
                    styles.controlButton,
                    state.currentPage <= 1 && styles.controlButtonDisabled,
                  ]}
                  accessibilityLabel="Previous page"
                >
                  <ChevronLeft
                    size={24}
                    color={state.currentPage <= 1 ? '#ccc' : '#333'}
                  />
                </TouchableOpacity>

                <Text style={styles.pageText}>
                  {state.currentPage} / {state.totalPages}
                </Text>

                <TouchableOpacity
                  onPress={goToNextPage}
                  disabled={state.currentPage >= state.totalPages}
                  style={[
                    styles.controlButton,
                    state.currentPage >= state.totalPages &&
                      styles.controlButtonDisabled,
                  ]}
                  accessibilityLabel="Next page"
                >
                  <ChevronRight
                    size={24}
                    color={
                      state.currentPage >= state.totalPages ? '#ccc' : '#333'
                    }
                  />
                </TouchableOpacity>
              </View>

              {/* Zoom Controls */}
              <View style={styles.zoomControls}>
                <TouchableOpacity
                  onPress={handleZoomOut}
                  disabled={state.scale <= 0.5}
                  style={[
                    styles.controlButton,
                    state.scale <= 0.5 && styles.controlButtonDisabled,
                  ]}
                  accessibilityLabel="Zoom out"
                >
                  <ZoomOut
                    size={20}
                    color={state.scale <= 0.5 ? '#ccc' : '#333'}
                  />
                </TouchableOpacity>

                <Text style={styles.zoomText}>
                  {Math.round(state.scale * 100)}%
                </Text>

                <TouchableOpacity
                  onPress={handleZoomIn}
                  disabled={state.scale >= 3.0}
                  style={[
                    styles.controlButton,
                    state.scale >= 3.0 && styles.controlButtonDisabled,
                  ]}
                  accessibilityLabel="Zoom in"
                >
                  <ZoomIn
                    size={20}
                    color={state.scale >= 3.0 ? '#ccc' : '#333'}
                  />
                </TouchableOpacity>
              </View>
            </View>
          )}
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  headerButton: {
    padding: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginHorizontal: 8,
  },
  content: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(245, 245, 245, 0.9)',
    zIndex: 10,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorIconContainer: {
    backgroundColor: '#FFEBEE',
    borderRadius: 50,
    padding: 16,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc3545',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
  },
  errorButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  downloadButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  closeErrorButton: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  closeErrorButtonText: {
    color: '#333',
    fontSize: 14,
    fontWeight: '500',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  pageControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  zoomControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  controlButton: {
    padding: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  controlButtonDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  pageText: {
    fontSize: 14,
    color: '#333',
    marginHorizontal: 8,
    minWidth: 60,
    textAlign: 'center',
  },
  zoomText: {
    fontSize: 14,
    color: '#333',
    marginHorizontal: 8,
    minWidth: 50,
    textAlign: 'center',
  },
});
